import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  Compass,
  BookOpen,
  Search,
  Clock,
  ArrowRight,
  Tag as TagIcon,
  Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BlogListPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Blog — Lojas Integradas";
    (async () => {
      try {
        const url = selectedTag ? `${API}/blog/posts?tag=${encodeURIComponent(selectedTag)}` : `${API}/blog/posts`;
        const res = await axios.get(url);
        setPosts(res.data.posts || []);
        setTags(res.data.tags || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedTag]);

  const filtered = q.trim()
    ? posts.filter(
        (p) =>
          p.title.toLowerCase().includes(q.toLowerCase()) ||
          p.excerpt.toLowerCase().includes(q.toLowerCase())
      )
    : posts;

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen bg-[#080C14] text-[#F8FAFC] selection:bg-[#D4AF37] selection:text-slate-950 font-sans">
      <BlogNav />

      {/* Header */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-20 pb-10 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-1.5 text-xs uppercase tracking-widest text-[#D4AF37] mb-6">
          <BookOpen className="w-3.5 h-3.5" /> Blog Maçônico
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white leading-[1.05]">
          Sabedoria, método e{" "}
          <span className="text-[#D4AF37]">gestão de Loja.</span>
        </h1>
        <p className="mt-5 text-slate-400 max-w-2xl mx-auto">
          Reflexões, guias práticos e boas práticas para Secretários, Tesoureiros,
          Chancelarias e Venerandos. Escrito por Irmãos, para Irmãos.
        </p>
      </section>

      {/* Search + tags */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 mb-10">
        <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              data-testid="blog-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por título ou tema…"
              className="w-full pl-11 pr-4 py-3 rounded-xl bg-slate-900/60 border border-slate-800 focus:border-[#D4AF37] outline-none text-sm text-slate-100"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="blog-tag-all"
              onClick={() => setSelectedTag(null)}
              className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
                !selectedTag
                  ? "bg-[#D4AF37] text-slate-950 border-[#D4AF37] font-bold"
                  : "border-slate-700 text-slate-400 hover:border-[#D4AF37]/50"
              }`}
            >
              Tudo
            </button>
            {tags.slice(0, 6).map((t) => (
              <button
                key={t}
                data-testid={`blog-tag-${t}`}
                onClick={() => setSelectedTag(t)}
                className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
                  selectedTag === t
                    ? "bg-[#D4AF37] text-slate-950 border-[#D4AF37] font-bold"
                    : "border-slate-700 text-slate-400 hover:border-[#D4AF37]/50"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-slate-500">Nenhum post encontrado.</div>
        ) : (
          <>
            {/* Featured */}
            {featured && (
              <Link
                to={`/blog/${featured.slug}`}
                data-testid={`post-featured-${featured.slug}`}
                className="group block mb-10 rounded-2xl bg-gradient-to-br from-[#0d1420] to-[#0a0f1a] border border-slate-800 hover:border-[#D4AF37]/40 p-8 sm:p-12 transition-all hover:-translate-y-0.5"
              >
                <div className="flex flex-col md:flex-row items-start gap-8">
                  <div className="text-8xl md:text-9xl leading-none">{featured.cover_emoji}</div>
                  <div className="flex-1">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(featured.tags || []).map((t) => (
                        <span key={t} className="text-[10px] uppercase tracking-widest text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-0.5 rounded">
                          {t}
                        </span>
                      ))}
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-serif font-bold text-white group-hover:text-[#D4AF37] transition-colors leading-tight">
                      {featured.title}
                    </h2>
                    <p className="mt-3 text-slate-400 leading-relaxed text-base">
                      {featured.excerpt}
                    </p>
                    <div className="mt-6 flex items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-3 h-3" /> {featured.read_min} min de leitura
                      </span>
                      <span>·</span>
                      <span>{featured.author}</span>
                      <span className="ml-auto text-[#D4AF37] group-hover:translate-x-1 transition-transform flex items-center gap-1 font-semibold">
                        Ler <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`post-card-${p.slug}`}
                  className="group rounded-2xl bg-[#0d1420]/70 border border-slate-800 hover:border-[#D4AF37]/40 p-6 flex flex-col hover:-translate-y-1 transition-all"
                >
                  <div className="text-5xl leading-none">{p.cover_emoji}</div>
                  <div className="mt-5 flex flex-wrap gap-1.5">
                    {(p.tags || []).slice(0, 2).map((t) => (
                      <span key={t} className="text-[9px] uppercase tracking-widest text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-0.5 rounded">
                        {t}
                      </span>
                    ))}
                  </div>
                  <h3 className="mt-3 text-lg font-serif font-bold text-white group-hover:text-[#D4AF37] transition-colors leading-snug">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed line-clamp-3 flex-1">
                    {p.excerpt}
                  </p>
                  <div className="mt-5 pt-4 border-t border-slate-800/60 flex items-center justify-between text-xs text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3 h-3" /> {p.read_min} min
                    </span>
                    <span className="text-[#D4AF37] group-hover:translate-x-0.5 transition-transform flex items-center gap-1 font-semibold text-[11px]">
                      LER <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </section>

      {/* CTA */}
      <section className="relative z-10 border-t border-slate-800/60 py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-serif font-bold text-white">
            Da teoria à prática em <span className="text-[#D4AF37]">5 minutos.</span>
          </h2>
          <p className="mt-3 text-slate-400 text-sm">
            Cadastre sua Loja gratuitamente e veja como o Lojas Integradas aplica cada uma dessas
            ideias.
          </p>
          <button
            data-testid="blog-cta-signup"
            onClick={() => navigate("/signup")}
            className="mt-8 px-8 py-3 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold uppercase tracking-wider text-sm rounded-xl inline-flex items-center gap-2"
          >
            Testar grátis <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <BlogFooter />
    </div>
  );
};

export const BlogNav = () => {
  const navigate = useNavigate();
  return (
    <nav className="relative z-10 border-b border-slate-800/60 backdrop-blur-md bg-[#080C14]/80 sticky top-0">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <button
          data-testid="blog-brand-link"
          onClick={() => navigate("/")}
          className="flex items-center gap-3 group"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8B6914] flex items-center justify-center shadow-lg shadow-[#D4AF37]/20 group-hover:scale-105 transition-transform">
            <Compass className="w-5 h-5 text-slate-950" strokeWidth={2.5} />
          </div>
          <div className="text-left">
            <div className="text-sm font-serif font-bold text-white leading-tight">
              Lojas Integradas
            </div>
            <div className="text-[10px] tracking-widest uppercase text-[#D4AF37]">
              Gestão Maçônica
            </div>
          </div>
        </button>
        <div className="flex items-center gap-4">
          <button
            data-testid="blog-nav-features"
            onClick={() => navigate("/features")}
            className="hidden sm:block text-sm text-slate-300 hover:text-[#D4AF37] transition-colors"
          >
            Recursos
          </button>
          <button
            data-testid="blog-nav-pricing"
            onClick={() => navigate("/pricing")}
            className="hidden sm:block text-sm text-slate-300 hover:text-[#D4AF37] transition-colors"
          >
            Planos
          </button>
          <button
            data-testid="blog-nav-signup"
            onClick={() => navigate("/signup")}
            className="text-sm px-5 py-2 rounded-lg bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold uppercase tracking-wider transition-all"
          >
            Cadastrar Loja
          </button>
        </div>
      </div>
    </nav>
  );
};

export const BlogFooter = () => (
  <footer className="relative z-10 border-t border-slate-800/60 py-8 px-6 text-center text-xs text-slate-500">
    <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
      <span>Lojas Integradas • Blog</span>
      <span className="font-mono text-[#D4AF37]">
        Liberdade • Igualdade • Fraternidade
      </span>
    </div>
  </footer>
);

export default BlogListPage;
